import clsx from 'clsx';
import { get } from 'lodash';
import moment from 'moment';
import { IconButton, Modal, Button, InputDate, Tooltip } from '@components';
import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDeal } from '../context';

const EXPIRIES = {
  LATE: 'LATE',
  UPCOMING: 'UPCOMING',
  CURRENT: 'CURRENT',
  UNDEFINED: 'UNDEFINED'
};

const EXPIRIES_CLASSES = {
  LATE: {
    bg: 'bg-red-50 hover:bg-red-100',
    text: 'text-red-600'
  },
  CURRENT: {
    bg: 'bg-amber-50 hover:bg-amber-100',
    text: 'text-amber-600'
  },
  UPCOMING: {
    bg: 'bg-slate-50 hover:bg-slate-100',
    text: 'text-slate-600'
  },
  UNDEFINED: {
    bg: 'bg-slate-50',
    text: 'text-slate-600'
  }
};

const expiryClasses = (date, bypass) =>
  EXPIRIES_CLASSES[resolveExpiry(date, bypass) || 'UNDEFINED'];

const resolveExpiry = (date = null, bypass = false) => {
  if (!date || bypass) return;

  const d = moment(date);
  const now = moment();

  if (!d.isValid()) {
    return;
  }

  if (d.isBefore(now.startOf('day'))) return EXPIRIES.LATE;

  if (d.isSameOrBefore(now.endOf('week'))) return EXPIRIES.CURRENT;

  return EXPIRIES.UPCOMING;
};

interface DataInterface {
  valid_start_at: string;
  valid_end_at: string;
}

export default function ExpiryColumn({ taskItem, onUpdate, filesCount = 0 }) {
  const [expiryIsOpened, setExpiryIsOpened] = useState(false);
  const { refreshing } = useDeal();

  const initialData = {
    valid_start_at: taskItem.valid_start_at || '',
    valid_end_at: taskItem.valid_end_at || ''
  };

  const [data, setData] = useState<DataInterface>(initialData);

  useEffect(() => {
    setData(initialData);
  }, [taskItem.updated_at]);

  const momentValidStartAt = data.valid_start_at && moment(data.valid_start_at);
  const momentValidEndAt = data.valid_end_at && moment(data.valid_end_at);

  const submittable =
    taskItem.valid_start_at !== data.valid_start_at || taskItem.valid_end_at !== data.valid_end_at;

  const rawExpireAt = taskItem.valid_end_at;
  const expireAt = rawExpireAt ? moment(rawExpireAt).format('D/M/YY') : null;

  const { bg: expiryBgClasses, text: expiryTextClasses } = expireAt
    ? expiryClasses(rawExpireAt, get(taskItem, 'state') === 'VALIDATED')
    : {};

  const templateExpirationInDays = Number(taskItem.document_expiration_in_days);

  const momentComputedEndAt =
    momentValidStartAt && templateExpirationInDays > 0
      ? momentValidStartAt.clone().add(templateExpirationInDays, 'days')
      : momentValidEndAt;

  const momentComputedStartAt =
    momentValidEndAt && !momentValidStartAt && templateExpirationInDays > 0
      ? momentValidEndAt.clone().subtract(templateExpirationInDays, 'days')
      : momentValidStartAt;

  const isError =
    moment.isMoment(momentComputedEndAt) &&
    moment.isMoment(momentComputedStartAt) &&
    momentComputedStartAt.isAfter(momentComputedEndAt);

  return (
    <div
      className={clsx(
        `!p-0 [&>div]:w-full [&>div]:flex [&>div]:justify-center [&>div]:items-center cell hidden lg:inline-flex justify-center items-center text-xs font-hmedium text-slate-500`,
        expiryBgClasses
      )}
    >
      <button
        type="button"
        onClick={() => setExpiryIsOpened(true)}
        className={`${expiryTextClasses} relative`}
      >
        {expireAt ? expireAt : '__/__/__'}
        {templateExpirationInDays > 0 && (
          <Tooltip
            content={
              <>
                Cette démarche à une durée de validité de {templateExpirationInDays} jours.
                <br />
                <u>Cliquez pour définir un délai d'expiration.</u>
              </>
            }
          >
            <div className="w-2 h-2 rounded-full bg-blue-600 -top-1 -right-0.5 absolute"></div>
          </Tooltip>
        )}
      </button>
      <Modal isOpen={expiryIsOpened} onClose={() => setExpiryIsOpened(false)}>
        <div className="w-[500px] rounded-lg bg-white divide-y">
          <div className="flex items-center justify-between px-6 py-3">
            <div className="flex flex-col">
              <h3 className="font-hmedium tracking-tight text-xl">Définir la date de validité</h3>
            </div>
            <div className="flex items-center space-x-2">
              <IconButton icon={XMarkIcon} onClick={() => setExpiryIsOpened(false)} size="lg" />
            </div>
          </div>

          <div className="p-6 flex flex-col space-y-6">
            <p className="text-slate-500 text-sm">
              Définir une date d'établissement et / ou de limite de validité permet de suivre l'état
              de validité et de créer des alertes sur le tableau de bord.
            </p>
            {Number(taskItem.document_expiration_in_days) > 0 && (
              <p className="bg-blue-50 border border-blue-600 text-blue-600 py-2 px-4 rounded-lg text-sm">
                Ce document a une{' '}
                <strong>durée de validité de {taskItem.document_expiration_in_days} jours</strong>.
              </p>
            )}
            <InputDate
              label="Date d'établissement du document (optionnel)"
              value={momentComputedStartAt}
              onChange={date => {
                const safeDate = date || new Date();
                return setData(prev => ({ ...prev, valid_start_at: safeDate }));
              }}
              classes={{ dropdown: 'z-[100]' }}
            />
            <InputDate
              label="Date limite de validité"
              value={momentComputedEndAt}
              onChange={date => {
                const safeDate = date || new Date();
                return setData(prev => ({ ...prev, valid_end_at: safeDate }));
              }}
              classes={{ dropdown: 'z-[100]' }}
            />
            {!!isError && (
              <p className="bg-red-50 border border-red-600 text-red-600 py-2 px-4 rounded-lg text-sm">
                La date de début ne peut pas être inférieure à la date de fin.
              </p>
            )}
          </div>
          <div className="flex items-center justify-between py-3 px-6">
            <div>
              {(!!momentValidStartAt || !!momentValidEndAt) && (
                <Button
                  icon={XMarkIcon}
                  label="Effacer"
                  onClick={() => {
                    setData({
                      valid_start_at: '',
                      valid_end_at: ''
                    });
                  }}
                />
              )}
            </div>
            <div>
              <Button
                disabled={!!isError}
                label="Enregistrer"
                loading={refreshing}
                color="blue"
                onClick={async () => {
                  await onUpdate({
                    valid_start_at: data.valid_start_at,
                    valid_end_at: momentComputedEndAt
                  });
                  setExpiryIsOpened(false);
                }}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
